import React from 'react'
import PropTypes from 'prop-types'
import OutboundLink from '../OutboundLink'
import './EvidenceChainEdge.css'

/**
 * Renders dataset/datapoint blocks as flexbox columns that wrap horizontally
 *
 * LAYOUT BEHAVIOR:
 * - Dataset blocks (ul elements) flow horizontally and wrap as needed
 * - Each block: min 120px, max 130px (allows ~4 blocks in 3-column edge span)
 * - Blocks use flexbox with 8px horizontal gap for visual coherence
 *
 * COLUMN SPLITTING (Single Dataset):
 * - If dataset has >2 datapoints: splits into 2 columns side-by-side
 * - Max width increases to 260px to accommodate 2 columns
 * - Columns use 8px gap between them
 *
 * SPECIAL FEATURES:
 * - Eye icon button for similarity scores (title contains "score" and "sim")
 * - Highlighted datasets get special background color
 * - Datapoint counts shown in parentheses if >1 (e.g., "IC50 = <500nM (3)")
 *
 * @param {Array} groupedDatapoints - Array of dataset groups with datapoints
 * @param {Set} highlighted - Set of highlighted dataset IDs
 * @param {boolean} hasSimilarity - Whether similarity comparison is available
 * @param {Function} onCompareClick - Callback for similarity comparison button
 */
const EvidenceChainEdge = ({ groupedDatapoints, highlighted = new Set(), hasSimilarity = false, onCompareClick = null }) => {
  /**
   * Formats datapoint title with count suffix if multiple datapoints grouped
   */
  const dpTitle = (dp) => {
    const count = dp.count > 1 ? ` (${dp.count})` : "";
    return dp.title ? `${dp.title}${count}` : "[title missing]";
  }

  /**
   * Renders a single datapoint list item with optional eye icon for similarity
   */
  const renderDatapoint = (dp, dpidx, hasSimilarity, onCompareClick) => {
    const title = dpTitle(dp);
    // Show eye icon if hasSimilarity flag is set (determined by SingleChain based on entity categories)
    const showEyeIcon = hasSimilarity;

    const handleClick = (e) => {
      if (showEyeIcon && onCompareClick) {
        e.preventDefault();
        e.stopPropagation();
        onCompareClick();
      }
    };

    return (
      <li
        key={dpidx}
        className="datapoints-info truncate-overflow truncate-lines-2"
      >
        {showEyeIcon && onCompareClick && (
          <button
            className="ev-ch-similarity-icon-btn"
            onClick={handleClick}
            title="Click to compare structures"
            aria-label="Compare structures"
          >
            <span className="glyphicon glyphicon-eye-open" />
          </button>
        )}
        {showEyeIcon ? (
          <span
            className="ev-ch-similarity-text"
            title={`${title}\n${dp.description}${dp.source_id ? ` (${dp.source_id})` : ''}`}
            onClick={handleClick}
          >
            {title}
          </span>
        ) : dp.url ? (
          <OutboundLink
            href={dp.url}
            title={`${title}\n${dp.description}`}
          >
            {title}
          </OutboundLink>
        ) : (
          <span title={`${title}\n${dp.description} (${dp.source_id})`}>
            {title}
          </span>
        )}
      </li>
    );
  };

  return (
    <div className="ev-ch-edge-list-wrp">
      {groupedDatapoints.map((group, idx) => {
        if (!group.dps.length) {
          return null;
        }

        // Only split into columns if this is a single dataset AND has >2 datapoints
        const isSingleDataset = groupedDatapoints.length === 1;
        const shouldSplitColumns = isSingleDataset && group.dps.length > 2;
        const midpoint = shouldSplitColumns ? Math.ceil(group.dps.length / 2) : group.dps.length;
        const firstColumn = group.dps.slice(0, midpoint);
        const secondColumn = shouldSplitColumns ? group.dps.slice(midpoint) : [];

        return (
          <ul className={`ev-ch-edge-list${shouldSplitColumns ? ' multi-column' : ''}`} key={idx}>
            {group.url ? (
              <OutboundLink
                className={`dataset-info${highlighted.has(group.dataset) ? " highlight" : ""}`}
                href={group.url}
                title={group.description}
              >
                {`${group.title} (${group.dpCount})`}
              </OutboundLink>
            ) : (
              <span className="dataset-info" title={group.description}>
                {group.title}
              </span>
            )}
            {shouldSplitColumns ? (
              <div className="datapoint-columns-wrapper">
                <div className="datapoint-column">
                  {firstColumn.map((dp, dpidx) => renderDatapoint(dp, dpidx, hasSimilarity, onCompareClick))}
                </div>
                <div className="datapoint-column">
                  {secondColumn.map((dp, dpidx) => renderDatapoint(dp, dpidx + midpoint, hasSimilarity, onCompareClick))}
                </div>
              </div>
            ) : (
              group.dps.map((dp, dpidx) => renderDatapoint(dp, dpidx, hasSimilarity, onCompareClick))
            )}
          </ul>
        );
      })}
      {groupedDatapoints.length === 0 && (
        <span className="alert-warning">Evidence temporarily unavailable</span>
      )}
    </div>
  );
};

EvidenceChainEdge.propTypes = {
  groupedDatapoints: PropTypes.arrayOf(
    PropTypes.shape({
      dataset: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      url: PropTypes.string,
      dps: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          description: PropTypes.string,
          url: PropTypes.string,
          count: PropTypes.number,
          source_id: PropTypes.string,
        })
      ).isRequired,
      dpCount: PropTypes.number,
    })
  ).isRequired,
  highlighted: PropTypes.instanceOf(Set),
};

export default EvidenceChainEdge;